import {
  INSTAGRAM_PERMISSIONS,
  MESSENGER_PERMISSIONS,
  META_APP_DASHBOARD_URL,
  META_BUSINESS_SETTINGS_URL,
  META_PERMISSIONS,
  META_STEP_STYLE
} from '@features/lead-management/constants/leadSettings';

export interface PermissionStep {
  text: string;
  permissions: string[];
}

export interface LinkStep {
  text: string;
  link: {
    text: string;
    url: string;
  };
  suffix: string;
}

export type Step = string | PermissionStep | LinkStep;

export const isPermissionStep = (step: Step): step is PermissionStep =>
  typeof step === 'object' && 'permissions' in step;

export const isLinkStep = (step: Step): step is LinkStep =>
  typeof step === 'object' && 'link' in step;

export const STEPS: Step[] = [
  {
    text: 'Go to the ',
    link: {
      text: 'Business Settings',
      url: META_BUSINESS_SETTINGS_URL
    },
    suffix: '.'
  },
  'Under Users, click on System Users.',
  'Select the admin system user, then click the Generate new token button.',
  {
    text: 'Select your app from the drop-down menu and select the following permissions from the list',
    permissions: META_PERMISSIONS
  },
  'Click the Generate token button.',
  'Copy and store your token securely — it will only be shown once.'
];

export const MESSENGER_STEPS: Step[] = [
  STEPS[0],
  'Under Users, click on System Users.',
  'Make sure to click Assign Assets and add the relevant facebook pages and the corresponding app to the system user.',
  'Select the admin system user, then click the Generate new token button.',
  {
    text: 'Select your app from the drop-down menu and select the following permissions from the list.',
    permissions: MESSENGER_PERMISSIONS
  },
  'Click the Generate token button.',
  'Copy and store your token securely — it will only be shown once.'
];

export const INSTAGRAM_STEPS: Step[] = [
  {
    text: 'Open your app in the ',
    link: { text: 'Meta App Dashboard', url: META_APP_DASHBOARD_URL },
    suffix: '.'
  },
  'Under Instagram, open API setup with Instagram business login and add your Instagram professional account.',
  {
    text: 'Click Generate token and allow the following permissions.',
    permissions: INSTAGRAM_PERMISSIONS
  },
  'Copy the Instagram app secret shown on the same page.',
  'Under Webhooks, add the webhook URL and verify token shown below, then subscribe to the messages field.',
  'Copy and store your token securely — it will only be shown once.'
];

export const PermissionList = ({
  permissions,
  className = ''
}: {
  permissions: string[];
  className?: string;
}) => (
  <ul
    className={`flex flex-col gap-1 rounded-lg bg-cofeeNeutralBg p-2 pl-6 text-sm text-cofeeNeutral10 ${className}`}
  >
    {permissions.map((permission) => (
      <li key={permission}>• {permission}</li>
    ))}
  </ul>
);

export const getStepKey = (step: Step): string => {
  if (typeof step === 'string') return step;
  if ('permissions' in step) return step.text;
  return step.link.text;
};

export interface StepItemClassNames {
  textClassOverride?: string;
  permissionListClassName?: string;
}

export const StepItem = ({
  step,
  index,
  classNames = {}
}: {
  step: Step;
  index: number;
  classNames?: StepItemClassNames;
}) => {
  const stepStyle = classNames.textClassOverride
    ? `${META_STEP_STYLE} ${classNames.textClassOverride}`
    : META_STEP_STYLE;

  if (isPermissionStep(step)) {
    return (
      <li className="flex flex-col gap-2">
        <div className="flex gap-2">
          <span className="font-bold text-cofeeNeutral10">{index + 1}.</span>
          <span className={stepStyle}>{step.text}</span>
        </div>
        <PermissionList
          permissions={step.permissions}
          className={classNames.permissionListClassName}
        />
      </li>
    );
  }

  if (isLinkStep(step)) {
    return (
      <li className="flex gap-2">
        <span className="font-bold text-cofeeNeutral10">{index + 1}.</span>
        <span className={stepStyle}>
          {step.text}
          <a
            href={step.link.url}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            {step.link.text}
          </a>
          {step.suffix}
        </span>
      </li>
    );
  }

  return (
    <li className="flex gap-2">
      <span className="font-bold text-cofeeNeutral10">{index + 1}.</span>
      <span className={stepStyle}>{step}</span>
    </li>
  );
};
